import posthog from 'posthog-js';

// PostHog configuration
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || '';
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://app.posthog.com';

// Initialize PostHog
export const initPostHog = () => {
  if (POSTHOG_KEY) {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      // Capture pageviews automatically
      capture_pageview: true,
      // Capture page leaves for session recording
      capture_pageleave: true,
      // Session recording configuration
      session_recording: {
        // Mask sensitive text inputs
        maskAllInputs: true,
        // Don't record password fields
        maskInputOptions: {
          password: true,
          email: true,
        },
      },
      // Enable session recording
      autocapture: true,
      // Disable in development if needed
      loaded: (posthog) => {
        if (process.env.NODE_ENV === 'development') {
          // You can disable certain features in dev
          // posthog.opt_out_capturing();
        }
      },
    });
  } else {
    console.warn('PostHog key not found. Analytics disabled.');
  }
};

// Track custom events
export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  if (POSTHOG_KEY) {
    posthog.capture(eventName, properties);
  }
};

// Identify user
export const identifyUser = (userId: string, properties?: Record<string, any>) => {
  if (POSTHOG_KEY) {
    posthog.identify(userId, properties);
  }
};

// Reset user (on logout)
export const resetUser = () => {
  if (POSTHOG_KEY) {
    posthog.reset();
  }
};

// Track feature usage
export const trackFeature = (featureName: string, metadata?: Record<string, any>) => {
  trackEvent(`feature_${featureName}`, {
    ...metadata,
    timestamp: new Date().toISOString(),
  });
};

// Track API calls
export const trackApiCall = (endpoint: string, success: boolean, duration?: number) => {
  trackEvent('api_call', {
    endpoint,
    success,
    duration,
    timestamp: new Date().toISOString(),
  });
};

// Track errors
export const trackError = (error: string, context?: Record<string, any>) => {
  trackEvent('error', {
    error_message: error,
    ...context,
    timestamp: new Date().toISOString(),
  });
};

// OrthoMind specific tracking
export const orthoTrack = {
  // Treatment Plan Events
  treatmentPlanGenerated: (sessionId: string, criteria?: any) => {
    trackEvent('treatment_plan_generated', {
      session_id: sessionId,
      criteria_count: criteria ? Object.keys(criteria).length : 0,
      has_criteria: !!criteria,
    });
  },

  treatmentPlanExported: (format: 'pdf' | 'word' | 'html', sessionId?: string) => {
    trackEvent('treatment_plan_exported', {
      format,
      session_id: sessionId,
    });
  },

  // Chat Events
  chatMessageSent: (messageType: 'text' | 'image', sessionLength: number) => {
    trackEvent('chat_message_sent', {
      message_type: messageType,
      session_length: sessionLength,
    });
  },

  questionAsked: (questionType: 'template' | 'custom', topic?: string) => {
    trackEvent('question_asked', {
      question_type: questionType,
      topic,
    });
  },

  // Feature Usage
  featureSelected: (feature: 'ask_questions' | 'treatment_plan' | 'scan_analysis' | 'deep_research') => {
    trackEvent('feature_selected', {
      feature,
    });
  },

  // Document Events
  documentEdited: (sessionId: string, wordCount?: number) => {
    trackEvent('document_edited', {
      session_id: sessionId,
      word_count: wordCount,
    });
  },

  documentRefreshed: (sessionId: string) => {
    trackEvent('document_refreshed', {
      session_id: sessionId,
    });
  },

  // Image Analysis
  imageAnalyzed: (imageCount: number, sessionId: string) => {
    trackEvent('image_analyzed', {
      image_count: imageCount,
      session_id: sessionId,
    });
  },

  // Session Events
  sessionStarted: (feature: string) => {
    trackEvent('session_started', {
      feature,
      start_time: new Date().toISOString(),
    });
  },

  sessionEnded: (feature: string, duration: number) => {
    trackEvent('session_ended', {
      feature,
      duration_seconds: duration,
    });
  },

  // Authentication Events
  userSignedIn: (method: 'email' | 'google' | 'other') => {
    trackEvent('user_signed_in', {
      method,
    });
  },

  userSignedUp: (method: 'email' | 'google' | 'other') => {
    trackEvent('user_signed_up', {
      method,
    });
  },

  userSignedOut: () => {
    trackEvent('user_signed_out', {});
  },

  // Error Events
  apiError: (endpoint: string, statusCode?: number, error?: string) => {
    trackEvent('api_error', {
      endpoint,
      status_code: statusCode,
      error_message: error,
    });
  },
};

export default posthog;